import {
  SHORTCUT_ACTIONS,
  platformOf,
  resolveShortcuts,
  type Platform,
  type ShortcutAction,
  type Shortcuts,
} from '@lyrmo/shared/shortcuts';
import { useCallback, useContext, useMemo, useSyncExternalStore } from 'react';
import { EditorShortcutsContext } from './shortcut-context';

const STORAGE_KEY = 'lyrmo.shortcuts';

type Changes = Partial<Record<ShortcutAction, string>>;

// Every editor on the page, and the shortcuts dialog, read the same keys and hear each change.
const listeners = new Set<() => void>();

function readStored(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '';
  } catch {
    // Storage can be unavailable, as in private windows.
    return '';
  }
}

function changesIn(stored: string): Changes {
  try {
    const parsed: unknown = JSON.parse(stored || '{}');
    if (typeof parsed !== 'object' || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        ([action, keys]) =>
          SHORTCUT_ACTIONS.some((known) => known === action) && typeof keys === 'string',
      ),
    );
  } catch {
    return {};
  }
}

function store(changes: Changes) {
  try {
    if (Object.keys(changes).length > 0) localStorage.setItem(STORAGE_KEY, JSON.stringify(changes));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Remembering is a convenience; without storage the defaults stay.
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Another tab changed them.
  const onStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The platform whose keys the site shows and listens for: ⌘ on a Mac, Ctrl elsewhere. */
export const currentPlatform = (): Platform => platformOf(navigator.userAgent);

export interface ShortcutSettings {
  readonly platform: Platform;
  readonly shortcuts: Shortcuts;
  /** The actions whose keys were changed from the default. */
  readonly changed: ReadonlySet<ShortcutAction>;
  readonly change: (action: ShortcutAction, keys: string) => void;
  /** Puts one action's keys back to the default, or every action's. */
  readonly reset: (action?: ShortcutAction) => void;
}

/** The editor's keyboard shortcuts: the defaults, with any changes remembered in this browser. */
export function useShortcuts(): ShortcutSettings {
  const host = useContext(EditorShortcutsContext);
  const stored = useSyncExternalStore(subscribe, readStored, () => '');
  const platform = currentPlatform();
  const changes = useMemo(() => changesIn(stored), [stored]);
  const shortcuts = useMemo(() => resolveShortcuts(changes, platform), [changes, platform]);
  const changed = useMemo(() => new Set(Object.keys(changes) as ShortcutAction[]), [changes]);

  const change = useCallback((action: ShortcutAction, keys: string) => {
    store({ ...changesIn(readStored()), [action]: keys });
  }, []);
  const reset = useCallback((action?: ShortcutAction) => {
    if (!action) {
      store({});
      return;
    }
    store(
      Object.fromEntries(
        Object.entries(changesIn(readStored())).filter(([changed]) => changed !== action),
      ),
    );
  }, []);

  return { platform, shortcuts, changed, change, reset, ...host };
}
